import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2, FileText, Upload, Check, ArrowRight, ArrowLeft,
  Globe, Users, Package, Shield, Loader2, CheckCircle, X, File
} from 'lucide-react';

const sectors = [
  'Agriculture',
  'Agro-Processing',
  'Manufacturing',
  'Textiles & Garments',
  'Pharmaceuticals',
  'Construction Materials',
  'Digital Services',
  'Creative Industries',
  'Mining & Minerals',
  'Other'
];

const countries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco',
  'Tanzania', 'Ethiopia', 'Rwanda', 'Senegal', "Cote d'Ivoire",
  'Cameroon', 'Uganda', 'Zimbabwe', 'Botswana', 'Namibia'
];

const employeeSizes = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '500+'
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    business_name: user?.company_name || '',
    registration_number: '',
    tax_id: '',
    country: user?.country || '',
    sector: '',
    sub_sector: '',
    export_readiness: 'beginner',
    primary_products: '',
    production_capacity: '',
    employee_size: '',
    years_in_business: '',
  });

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('atff_token');
    if (!token) {
      toast.error('Please log in to upload documents');
      return;
    }

    setUploading(true);

    for (const file of files) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Supported: PDF, JPG, PNG`);
        continue;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large. Maximum size is 10MB`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('document_type', 'business_document');

        const response = await axios.post(`${API}/documents/upload`, formDataUpload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        setUploadedFiles(prev => [...prev, response.data.document]);
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = async (documentId) => {
    const token = localStorage.getItem('atff_token');
    try {
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadedFiles(prev => prev.filter(f => f.document_id !== documentId));
      toast.success('Document removed');
    } catch (error) {
      toast.error('Failed to remove document');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      // Create a synthetic event to reuse handleFileSelect
      handleFileSelect({ target: { files } });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.business_name && formData.registration_number && formData.country;
      case 2:
        return formData.sector;
      case 3:
        return true; // Documents optional
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateStep()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('atff_token');
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        // withCredentials removed for CORS compatibility
      };

      const payload = {
        business_name: formData.business_name,
        registration_number: formData.registration_number,
        tax_id: formData.tax_id || null,
        country: formData.country,
        sector: formData.sector,
        sub_sector: formData.sub_sector || null,
        export_readiness: formData.export_readiness || 'beginner',
        primary_products: formData.primary_products ? formData.primary_products.split(',').map(p => p.trim()).filter(Boolean) : [],
        production_capacity: formData.production_capacity || null,
        employee_size: formData.employee_size || null,
        years_in_business: formData.years_in_business ? parseInt(formData.years_in_business, 10) : null,
      };

      await axios.post(`${API}/businesses`, payload, config);
      
      setCompleted(true);
      toast.success('Business profile created successfully!');
    } catch (error) {
      console.error('Profile creation error:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Profile already exists');
        navigate('/dashboard');
      } else {
        toast.error('Failed to create profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / 4) * 100;

  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container-main py-20">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-charcoal mb-4 font-poppins">
              Profile Submitted!
            </h1>
            <p className="text-gray-600 mb-8">
              Your business profile has been submitted for verification. 
              You'll be notified once your National Trade Node reviews your application.
            </p>
            <Badge className="bg-tradeGold text-black mb-8">
              Verification Pending
            </Badge>
            <div className="flex justify-center gap-4">
              <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => navigate('/marketplace')} variant="outline">
                Browse Marketplace
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container-main py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge className="bg-continental/10 text-continental mb-4">Business Onboarding</Badge>
            <h1 className="text-3xl font-bold text-charcoal font-poppins mb-2">
              Create Your Business Profile
            </h1>
            <p className="text-gray-600">
              Complete your profile to get verified and start trading across Africa
            </p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Step {step} of 4</span>
              <span className="text-sm font-medium text-continental">{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2" />
            
            <div className="flex justify-between mt-4">
              {['Business Info', 'Industry', 'Documents', 'Review'].map((label, i) => (
                <div key={label} className={`flex flex-col items-center ${i + 1 <= step ? 'text-continental' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    i + 1 < step ? 'bg-continental text-white' : 
                    i + 1 === step ? 'border-2 border-continental text-continental' : 
                    'border-2 border-gray-300 text-gray-400'
                  }`}>
                    {i + 1 < step ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className="text-xs mt-1 hidden sm:block">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step 1: Business Info */}
          {step === 1 && (
            <Card data-testid="onboarding-step-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-continental" />
                  Business Information
                </CardTitle>
                <CardDescription>Basic details about your business</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Business Name *</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => handleChange('business_name', e.target.value)}
                    placeholder="Enter your business name"
                    className="mt-1"
                    data-testid="business-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Registration Number *</Label>
                    <Input
                      value={formData.registration_number}
                      onChange={(e) => handleChange('registration_number', e.target.value)}
                      placeholder="e.g., RC-123456"
                      className="mt-1"
                      data-testid="registration-number-input"
                    />
                  </div>
                  <div>
                    <Label>Tax ID</Label>
                    <Input
                      value={formData.tax_id}
                      onChange={(e) => handleChange('tax_id', e.target.value)}
                      placeholder="e.g., TIN-12345678"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Country of Operation *</Label>
                  <Select value={formData.country} onValueChange={(v) => handleChange('country', v)}>
                    <SelectTrigger className="mt-1" data-testid="country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={nextStep} className="btn-primary">
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Industry */}
          {step === 2 && (
            <Card data-testid="onboarding-step-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-continental" />
                  Industry & Products
                </CardTitle>
                <CardDescription>Tell us about your business activities</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Primary Sector *</Label>
                  <Select value={formData.sector} onValueChange={(v) => handleChange('sector', v)}>
                    <SelectTrigger className="mt-1" data-testid="sector-select">
                      <SelectValue placeholder="Select your sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {sectors.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Primary Products/Services</Label>
                  <Textarea
                    value={formData.primary_products}
                    onChange={(e) => handleChange('primary_products', e.target.value)}
                    placeholder="List your main products (comma separated)"
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Employee Size</Label>
                    <Select value={formData.employee_size} onValueChange={(v) => handleChange('employee_size', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        {employeeSizes.map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Years in Business</Label>
                    <Input
                      type="number"
                      value={formData.years_in_business}
                      onChange={(e) => handleChange('years_in_business', e.target.value)}
                      placeholder="e.g., 5"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Export Readiness</Label>
                  <Select value={formData.export_readiness} onValueChange={(v) => handleChange('export_readiness', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner - New to exporting</SelectItem>
                      <SelectItem value="intermediate">Intermediate - Some export experience</SelectItem>
                      <SelectItem value="advanced">Advanced - Regular exporter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-between pt-4">
                  <Button onClick={prevStep} variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={nextStep} className="btn-primary">
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Documents */}
          {step === 3 && (
            <Card data-testid="onboarding-step-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-continental" />
                  Business Documents
                </CardTitle>
                <CardDescription>Upload documents for verification (optional for now)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  className="hidden"
                  data-testid="document-file-input"
                />

                {/* Drag & Drop Zone */}
                <div 
                  className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-continental hover:bg-green-50/30 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  data-testid="document-dropzone"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-12 h-12 text-continental mx-auto mb-4 animate-spin" />
                      <p className="text-gray-600 mb-2">Uploading documents...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Drag & drop files here or click to browse</p>
                      <p className="text-sm text-gray-500 mb-4">
                        Supported: PDF, JPG, PNG (Max 10MB each)
                      </p>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        data-testid="upload-documents-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Documents
                      </Button>
                    </>
                  )}
                </div>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-charcoal">Uploaded Documents ({uploadedFiles.length})</h4>
                    {uploadedFiles.map(file => (
                      <div 
                        key={file.document_id} 
                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <File className="w-5 h-5 text-continental" />
                          <div>
                            <p className="text-sm font-medium text-charcoal">{file.original_filename}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.file_size)}</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFile(file.document_id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-oceanBlue mb-2">Recommended Documents:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Business Registration Certificate</li>
                    <li>• Tax Clearance Certificate</li>
                    <li>• Export License (if applicable)</li>
                    <li>• Product Certifications</li>
                  </ul>
                </div>

                <div className="flex justify-between pt-4">
                  <Button onClick={prevStep} variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={nextStep} className="btn-primary">
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <Card data-testid="onboarding-step-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-continental" />
                  Review & Submit
                </CardTitle>
                <CardDescription>Review your information before submitting</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-charcoal mb-3">Business Information</h4>
                    <dl className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-gray-500">Business Name</dt>
                        <dd className="font-medium">{formData.business_name}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Registration Number</dt>
                        <dd className="font-medium">{formData.registration_number}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Country</dt>
                        <dd className="font-medium">{formData.country}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Sector</dt>
                        <dd className="font-medium">{formData.sector}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="bg-amber-50 p-4 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Your profile will be reviewed by your National Trade Node 
                      for verification. This typically takes 2-5 business days.
                    </p>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button onClick={prevStep} variant="outline">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button 
                      onClick={handleSubmit} 
                      className="btn-primary"
                      disabled={loading}
                      data-testid="submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit for Verification
                          <Check className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
